<template>
  <div class="text-center">
    <v-btn color="primary" @click="dialog = true" data-cy="add-lead-btn">
      New Lead
    </v-btn>

    <v-dialog v-model="dialog" persistent width="auto" data-cy="new-lead-modal">
      <v-card>
        <v-card-title>
          <span>New Lead</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-form ref="form" lazy-validation>
              <v-row>
                <v-col cols="12" sm="6" md="6">
                  <v-text-field
                    label="School name*"
                    required
                    :rules="[rules.required]"
                    v-model="lead.school_name"
                    data-cy="lead-school-name"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="6">
                  <v-combobox
                    label="District*"
                    v-model="lead.district_name"
                    variant="outlined"
                    :items="districts"
                    required
                    :rules="[rules.required]"
                    data-cy="lead-district-name"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="6">
                  <v-text-field
                    v-model="lead.firstname"
                    label="First name*"
                    required
                    :rules="[rules.required]"
                    clearable
                    data-cy="lead-first-name"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="6">
                  <v-text-field
                    v-model="lead.lastname"
                    label="Last name*"
                    required
                    :rules="[rules.required]"
                    clearable
                    data-cy="lead-last-name"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="6">
                  <v-text-field
                    label="Email*"
                    v-model="lead.email"
                    :rules="[rules.required, rules.email]"
                    required
                    data-cy="lead-email"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="6">
                  <v-text-field
                    label="Phone*"
                    v-model="lead.phone_number"
                    required
                    :rules="[rules.required, rules.maxLength(20)]"
                    data-cy="lead-phone-number"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="lead.timezone"
                    :items="timeZones"
                    label="Time Zone"
                    clearable
                    data-cy="lead-timezone"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model.number="lead.num_students"
                    label="Student Enrollment"
                    hint="Optional"
                    persistent-hint
                    :rules="[rules.numberOptional, rules.integerOptional]"
                    type="number"
                    step="1"
                    data-cy="lead-student-enrollment"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="lead.address1"
                    label="Address 1"
                    hint="Recommended for quotes"
                    persistent-hint
                    data-cy="lead-address1"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="lead.address2"
                    label="Address 2"
                    data-cy="lead-address2"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field v-model="lead.city" label="City" data-cy="lead-city" />
                </v-col>
                <v-col cols="6">
                  <v-text-field v-model="lead.state" label="State" data-cy="lead-state" />
                </v-col>
                <v-col cols="6">
                  <v-text-field v-model="lead.zipcode" label="Zipcode" data-cy="lead-zipcode" />
                </v-col>
                <v-col cols="6">
                  <v-text-field v-model="lead.country" label="Country" data-cy="lead-country" />
                </v-col>
              </v-row>
            </v-form>
          </v-container>
          <small>*indicates required field</small>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            variant="flat"
            @click="validate"
            :disabled="isLoading"
            :loading="isLoading"
            data-cy="save-lead-btn"
          >
            {{ isLoading ? "Saving..." : "Save Lead" }}
          </v-btn>
          <v-btn
            color="primary"
            variant="outlined"
            @click="dialog = false"
            data-cy="cancel-lead-btn"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;

.v-card-title {
  background-color: $primary;
  color: white;
}
</style>

<script setup lang="ts">
import type { Lead } from "@/api_types/school";
import { useAlertsStore } from "@/stores/alerts";
import useSchools from "@/stores/support/schools";
import useLeads from "@/stores/support/leads";
import { ref, computed } from "vue";

const alertStore = useAlertsStore();
const schoolStore = useSchools();
const leadsStore = useLeads();
const form = ref();

const emptyLead = (): Lead => ({
  school_name: "",
  district_name: "",
  firstname: "",
  lastname: "",
  email: "",
  phone_number: "",
  num_students: undefined,
  timezone: undefined,
  address1: "",
  address2: "",
  city: "",
  state: "",
  zipcode: "",
  country: "",
  request_type: "lead",
});

const lead = ref<Lead>(emptyLead());
const dialog = ref(false);
const isLoading = ref(false);

const timeZones = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
];

const districts = computed(() =>
  schoolStore.districts.data.map((district) => district.district_name)
);

const rules = {
  required: (value: string) => !!value || "Field is required",
  email: (value: string) => /.+@.+/.test(value) || "Invalid email address",
  numberOptional: (value: unknown) =>
    value === null ||
    value === undefined ||
    value === "" ||
    (!isNaN(parseFloat(String(value))) && isFinite(Number(value))) ||
    "Must be a number",
  integerOptional: (value: unknown) =>
    value === null ||
    value === undefined ||
    value === "" ||
    Number.isInteger(Number(value)) ||
    "Must be a whole number",
  maxLength: (max: number) => (value: string) =>
    !value || String(value).length <= max || `Must be ${max} characters or fewer`,
};

const resetForm = () => {
  lead.value = emptyLead();
};

const createLead = async () => {
  isLoading.value = true;
  try {
    const payload: Lead = {
      ...lead.value,
      request_type: "lead",
    };
    // Omit empty optional fields
    if (!payload.timezone) delete payload.timezone;
    if (payload.num_students === undefined || payload.num_students === ("" as any)) {
      delete payload.num_students;
    }

    const res = await leadsStore.createLead(payload);
    if (res.status) {
      alertStore.addAlert("success", "New Lead Created", 5);
      dialog.value = false;
      resetForm();
    } else {
      alertStore.addAlert("error", res.message || "Failed to create lead", 10);
    }
  } catch (error) {
    console.error("Error creating lead:", error);
    alertStore.addAlert("error", "Failed to create lead", 10);
  } finally {
    isLoading.value = false;
  }
};

const validate = async () => {
  const { valid } = await form.value.validate();
  if (valid) {
    createLead();
  }
};
</script>
